import { Chess } from 'chess.js';

// ── Material values ─────────────────────────────────────────────────────
const PIECE_VALUES = { p: 100, n: 320, b: 330, r: 500, q: 900, k: 0 };

// Small centrality bonus — pieces in the center are worth a bit more.
// Encourages the bot to fight for the center like a real player would.
const CENTER_BONUS = {
  0: 0, 1: 4, 2: 8, 3: 12,
  4: 12, 5: 8, 6: 4, 7: 0,
};

/**
 * Bot "personality" scales directly with the human player's CURRENT
 * rating — exactly as requested: high rating → deep, aggressive, highly
 * accurate bot. Mid rating → balanced bot. Low rating → shallow,
 * blunder-prone, gentle bot. Because it re-reads the player's live
 * rating every game, the bot automatically gets tougher as the player
 * improves (or eases up if they're on a losing streak).
 */
export function getBotDifficulty(playerRating) {
  if (playerRating >= 1700) {
    return { depth: 3, blunderChance: 0.03, aggression: 1.6, label: 'Master Bot', thinkMs: 2200 };
  }
  if (playerRating >= 1400) {
    return { depth: 2, blunderChance: 0.10, aggression: 1.15, label: 'Strong Bot', thinkMs: 1800 };
  }
  if (playerRating >= 1100) {
    return { depth: 2, blunderChance: 0.22, aggression: 0.9, label: 'Club Bot', thinkMs: 1400 };
  }
  return { depth: 1, blunderChance: 0.40, aggression: 0.6, label: 'Learner Bot', thinkMs: 1000 };
}

function evaluateBoard(game, aggression) {
  if (game.isCheckmate()) {
    // The side whose turn it is has been checkmated.
    return game.turn() === 'w' ? -100000 : 100000;
  }
  if (game.isDraw() || game.isStalemate() || game.isThreefoldRepetition()) return 0;

  let score = 0;
  const board = game.board();
  for (let r = 0; r < 8; r++) {
    for (let f = 0; f < 8; f++) {
      const sq = board[r][f];
      if (!sq) continue;
      let val = PIECE_VALUES[sq.type];
      val += (CENTER_BONUS[r] + CENTER_BONUS[f]) * (sq.type === 'p' || sq.type === 'n' ? 0.6 : 0.3);
      score += sq.color === 'w' ? val : -val;
    }
  }

  // Mobility / aggression — the stronger the bot, the more it values
  // having lots of active options and pressuring the opponent.
  const mobility = game.moves().length;
  score += (game.turn() === 'w' ? mobility : -mobility) * aggression;

  // Small bonus for giving check — makes stronger bots feel more forcing.
  if (game.isCheck()) {
    score += (game.turn() === 'w' ? -1 : 1) * 15 * aggression;
  }

  return score;
}

function minimax(game, depth, alpha, beta, maximizing, aggression) {
  if (depth === 0 || game.isGameOver()) {
    return evaluateBoard(game, aggression);
  }
  const moves = game.moves();
  if (maximizing) {
    let best = -Infinity;
    for (const m of moves) {
      game.move(m);
      best = Math.max(best, minimax(game, depth - 1, alpha, beta, false, aggression));
      game.undo();
      alpha = Math.max(alpha, best);
      if (beta <= alpha) break;
    }
    return best;
  } else {
    let best = Infinity;
    for (const m of moves) {
      game.move(m);
      best = Math.min(best, minimax(game, depth - 1, alpha, beta, true, aggression));
      game.undo();
      beta = Math.min(beta, best);
      if (beta <= alpha) break;
    }
    return best;
  }
}

/**
 * Compute the bot's move for the given position.
 * @param {string} fen - current position
 * @param {number} playerRating - human player's current rating (drives difficulty)
 * @returns {{from,to,promotion,san}|null}
 */
export function getBotMove(fen, playerRating) {
  const game = new Chess(fen);
  const legalMoves = game.moves({ verbose: true });
  if (legalMoves.length === 0) return null;

  const diff = getBotDifficulty(playerRating);

  // Blunder chance — even strong human-like players occasionally slip;
  // weaker bots do it a lot more, which is what makes them feel "low level"
  // rather than just slow.
  if (Math.random() < diff.blunderChance) {
    const randomMove = legalMoves[Math.floor(Math.random() * legalMoves.length)];
    return randomMove;
  }

  const botColor = game.turn();
  const maximizing = botColor === 'w';
  let bestMoves = [];
  let bestScore = maximizing ? -Infinity : Infinity;

  for (const m of legalMoves) {
    game.move(m);
    const score = minimax(game, diff.depth - 1, -Infinity, Infinity, !maximizing, diff.aggression);
    game.undo();

    if (maximizing ? score > bestScore : score < bestScore) {
      bestScore = score;
      bestMoves = [m];
    } else if (score === bestScore) {
      bestMoves.push(m);
    }
  }

  // Pick randomly among equally-good top moves so the bot doesn't feel
  // robotically identical every single game.
  return bestMoves[Math.floor(Math.random() * bestMoves.length)] || legalMoves[0];
}

/** A believable "virtual rating" for the bot at this difficulty, used
 * purely for the Elo calculation so beating a tougher bot is worth more. */
export function getBotVirtualRating(playerRating) {
  // The bot mirrors the player's own rating — this keeps every bot game
  // a fair ~50/50 fight, and naturally keeps toughening as the player
  // improves (or easing off during a slump).
  return playerRating;
}

/**
 * Full-strength analysis of a single position, used by Game Review.
 * Scores EVERY legal move (white-positive centipawns) with the same
 * minimax the strongest bot uses — no blunder randomness, fixed depth —
 * so the review can compare the move that was PLAYED against the BEST
 * move that was available.
 *
 * @param {string} fen
 * @param {number} depth - search depth (2 keeps review fast on phones)
 * @returns {{ best: {san,from,to,score}, scores: Object<string,number>, turn: 'w'|'b' } | null}
 */
export function analyzePosition(fen, depth = 2) {
  let game;
  try { game = new Chess(fen); } catch { return null; }
  const legal = game.moves({ verbose: true });
  if (legal.length === 0) return null;

  const turn = game.turn();
  const maximizing = turn === 'w';
  const scores = {};
  let best = null;

  for (const m of legal) {
    game.move(m);
    const s = minimax(game, depth - 1, -Infinity, Infinity, !maximizing, 1.1);
    game.undo();
    scores[m.san] = s;
    if (!best || (maximizing ? s > best.score : s < best.score)) {
      best = { san: m.san, from: m.from, to: m.to, score: s };
    }
  }

  return { best, scores, turn };
}
